import { useState, useCallback, useMemo } from 'react';
import {
  TimeToValueInputs,
  AttributionFactors,
  ImplementationInvestment,
} from '../types';
import { getScenarioPresets, applyPilotModeAdjustments } from '../services/timeToValueService';

export interface UseTimeToValueReturn {
  timeToValue: TimeToValueInputs;
  attributionFactors: AttributionFactors;
  implementationInvestment: ImplementationInvestment;
  scenarioMode: 'conservative' | 'moderate' | 'optimistic';
  pilotMode: boolean;
  
  updateTimeToValue: (updates: Partial<TimeToValueInputs>) => TimeToValueInputs;
  updateAttributionFactors: (updates: Partial<AttributionFactors>) => AttributionFactors;
  updateImplementationInvestment: (updates: Partial<ImplementationInvestment>) => ImplementationInvestment;
  setScenarioMode: (mode: 'conservative' | 'moderate' | 'optimistic') => void;
  setPilotMode: (enabled: boolean) => void;
  applyScenarioPreset: (mode: 'conservative' | 'moderate' | 'optimistic', includeTimeline?: boolean) => ('minimal' | 'standard' | 'comprehensive' | undefined);
  resetToDefaults: () => void;
  
  validationErrors: string[];
}

const defaultTimeToValue: TimeToValueInputs = {
  implementationWeeks: 6,    // Default to Standard (6 weeks / 1-2 months) - matches balanced/moderate scenario
  adoptionRampMonths: 2,     // Standard adoption ramp for moderate scenario
  operationalRealizationFactor: 0.8,
  earlyWinsEnabled: false,
};

const defaultAttributionFactors: AttributionFactors = {
  ttfAttributionFactor: 0.2,
  roleVariabilityDiscount: 0.15,  // Reduced from 0.25 since users now provide specific hiring profiles
};

const defaultImplementationInvestment: ImplementationInvestment = {
  mode: 'auto',
};

export function useTimeToValue(
  industry?: string,
  annualHires?: number
): UseTimeToValueReturn {
  const [timeToValue, setTimeToValue] = useState<TimeToValueInputs>(defaultTimeToValue);
  const [attributionFactors, setAttributionFactors] = useState<AttributionFactors>(defaultAttributionFactors);
  const [implementationInvestment, setImplementationInvestment] = useState<ImplementationInvestment>(defaultImplementationInvestment);
  const [scenarioMode, setScenarioModeState] = useState<'conservative' | 'moderate' | 'optimistic'>('moderate');
  const [pilotMode, setPilotModeState] = useState(false);

  // Update time-to-value inputs and return new value
  const updateTimeToValue = useCallback((updates: Partial<TimeToValueInputs>) => {
    const newValue = { ...timeToValue, ...updates };
    setTimeToValue(newValue);
    return newValue;
  }, [timeToValue]);

  // Update attribution factors and return new value
  const updateAttributionFactors = useCallback((updates: Partial<AttributionFactors>) => {
    const newValue = { ...attributionFactors, ...updates };
    setAttributionFactors(newValue);
    return newValue;
  }, [attributionFactors]);

  // Update implementation investment and return new value
  const updateImplementationInvestment = useCallback((updates: Partial<ImplementationInvestment>) => {
    const newValue = { ...implementationInvestment, ...updates };
    setImplementationInvestment(newValue);
    return newValue;
  }, [implementationInvestment]);

  // Apply scenario preset - can optionally include timeline settings
  const applyScenarioPreset = useCallback((mode: 'conservative' | 'moderate' | 'optimistic', includeTimeline: boolean = false) => {
    const presets = getScenarioPresets(mode, includeTimeline);
    
    if (includeTimeline) {
      // Apply all settings including timeline
      setTimeToValue(prev => ({
        ...prev,
        ...presets.timeToValue,
      }));
    } else {
      // Apply all preset values but preserve only implementation weeks if manually changed
      setTimeToValue(prev => ({
        ...prev,
        ...presets.timeToValue,
        // Only preserve implementation weeks when manually changed
        implementationWeeks: prev.implementationWeeks,
        // adoptionRampMonths comes from preset and is NOT preserved
      }));
    }
    
    setAttributionFactors(prev => ({
      ...prev,
      ...presets.attributionFactors,
    }));
    
    setScenarioModeState(mode);
    
    // Return the investment approach if timeline was included
    return presets.investmentApproach;
  }, []);

  // Set scenario mode
  const setScenarioMode = useCallback((mode: 'conservative' | 'moderate' | 'optimistic') => {
    applyScenarioPreset(mode);
  }, [applyScenarioPreset]);

  // Set pilot mode
  const setPilotMode = useCallback((enabled: boolean) => {
    setPilotModeState(enabled);
    
    if (enabled) {
      const adjusted = applyPilotModeAdjustments(timeToValue, attributionFactors);
      setTimeToValue(adjusted.timeToValue);
      setAttributionFactors(adjusted.attributionFactors);
    }
  }, [timeToValue, attributionFactors]);

  // Reset to defaults
  const resetToDefaults = useCallback(() => {
    setTimeToValue(defaultTimeToValue);
    setAttributionFactors(defaultAttributionFactors);
    setImplementationInvestment(defaultImplementationInvestment);
    setScenarioModeState('moderate');
    setPilotModeState(false);
  }, []);

  // Validation
  const validationErrors = useMemo(() => {
    const errors: string[] = [];
    
    // Validate implementation weeks
    if (timeToValue.implementationWeeks < 4 || timeToValue.implementationWeeks > 52) {
      errors.push('Implementation duration must be between 4 and 52 weeks');
    }
    
    // Validate adoption ramp
    const maxAdoptionMonths = 12; // Extended adoption period up to 12 months
    if (timeToValue.adoptionRampMonths < 0 || timeToValue.adoptionRampMonths > maxAdoptionMonths) {
      errors.push(`Adoption ramp must be between 0 and 12 months`);
    }
    
    // Validate operational realization factor
    if (timeToValue.operationalRealizationFactor < 0.5 || timeToValue.operationalRealizationFactor > 1) {
      errors.push('Operational realization factor must be between 50% and 100%');
    }
    
    // Validate attribution factors
    if (attributionFactors.ttfAttributionFactor < 0 || attributionFactors.ttfAttributionFactor > 1) {
      errors.push('TTF attribution factor must be between 0% and 100%');
    }
    
    if (attributionFactors.roleVariabilityDiscount < 0 || attributionFactors.roleVariabilityDiscount > 1) {
      errors.push('Role variability discount must be between 0% and 100%');
    }
    
    // Validate custom investment if in custom mode
    if (implementationInvestment.mode === 'custom') {
      if (!implementationInvestment.customAmount && 
          !implementationInvestment.sponsorHours && 
          !implementationInvestment.opsHours && 
          !implementationInvestment.itHours && 
          !implementationInvestment.trainingHours) {
        errors.push('Please enter either a custom amount or role allocations');
      }
    }
    
    return errors;
  }, [timeToValue, attributionFactors, implementationInvestment]);

  return {
    timeToValue,
    attributionFactors,
    implementationInvestment,
    scenarioMode,
    pilotMode,
    updateTimeToValue,
    updateAttributionFactors,
    updateImplementationInvestment,
    setScenarioMode,
    setPilotMode,
    applyScenarioPreset,
    resetToDefaults,
    validationErrors,
  };
}